import { Box, Button, CircularProgress, Divider, TextField, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadImage from '../images/icons/upload-image.png';
import { useCategoryForm } from "../hooks/UseCategoryForm";
import { useWaits } from "../hooks/UseWait";
import Fetch from "../services/Fetch";
import { useConstants } from "../hooks/UseConstants";
import { buildCategoryFormData } from "../helper/CategoryFormData";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import MultipleSelectChip from "../components/MultiSelect";

function UpdateCategory({ onClickClose, setSnackbar, category, setCategory, getCategories }) {
    const intl = useIntl();
    const { language, host } = useConstants();
    const theme = useTheme();
    const { sendWait, setSendWait } = useWaits();
    const {
        nameEn, setNameEn, nameAr, setNameAr,
        descriptionEn, setDescriptionEn, descriptionAr, setDescriptionAr,
        image, setImage, majorIds, setMajorIds
    } = useCategoryForm();
    const [majors, setMajors] = useState([]);
    const [imagePreview, setImagePreview] = useState('');

    const loadMajors = async () => {
        let all = [];
        let page = 1;
        let lastPage = 1;

        do {
            const result = await Fetch(host + `/majors?page=${page}&per_page=50`, 'GET', null);
            if (result.status !== 200) break;
            const payload = result.data?.data;
            const items = Array.isArray(payload) ? payload : (payload?.data || []);
            all = [...all, ...items];
            lastPage = result.data.pagination?.last_page || payload?.last_page || 1;
            page++;
        } while (page <= lastPage);

        setMajors(all);
    };

    const setInputs = () => {
        if (!category) return;
        setNameEn(category.name_en || '');
        setNameAr(category.name_ar || '');
        setDescriptionEn(category.description_en || '');
        setDescriptionAr(category.description_ar || '');
        setImagePreview(category.image || '');
        setMajorIds(category.majors?.map((m) => m.id) || []);
        setImage('');
    };

    useEffect(() => {
        loadMajors();
    }, []);

    useEffect(() => {
        setInputs();
    }, [category]);

    const updateCategory = async () => {
        setSendWait(true);

        const formData = buildCategoryFormData({
            nameEn, nameAr, descriptionEn, descriptionAr, image, majorIds
        });

        const result = await Fetch(host + `/admin/categories/${category.id}/update`, 'POST', formData);

        if (result.status === 200) {
            setSnackbar('success', <FormattedMessage id="category_updated_msg" />);
            await getCategories();
            setCategory('');
            onClickClose();
        } else if (result.status === 422) {
            const err = result.data.errors?.[0] || result.data.message;
            setSnackbar('error', err);
        }

        setSendWait(false);
    };

    if (!category) return null;

    return (
        <Box sx={{ backgroundColor: theme.palette.background.paper }} className="shadow-lg w-3/5 h-screen rounded-3xl px-4 py-5 overflow-y-scroll none-view-scroll max-sm:w-4/5 max-sm:translate-x-0 max-sm:left-0 relative max-sm:h-screen" dir={language === 'en' ? 'ltr' : "rtl"}>
            <Typography variant="h5" className="!font-semibold max-sm:!text-xl"><FormattedMessage id="category_update" /></Typography>
            <CloseIcon onClick={() => { onClickClose(); }} className="text-gray-700 cursor-pointer absolute top-5 left-5" sx={{ left: language === 'en' && '90%' }} fontSize="large" />
            <Divider className="!my-5" />
            <TextField variant="outlined" label={<FormattedMessage id="category_name_ar" />} className="w-full" onChange={(e) => setNameAr(e.target.value)} value={nameAr} />
            <TextField variant="outlined" label={<FormattedMessage id="category_name_en" />} className="w-full !mt-5" onChange={(e) => setNameEn(e.target.value)} value={nameEn} />
            <TextField variant="outlined" label={<FormattedMessage id="category_description_ar" />} className="w-full !mt-5" multiline minRows={2} onChange={(e) => setDescriptionAr(e.target.value)} value={descriptionAr} />
            <TextField variant="outlined" label={<FormattedMessage id="category_description_en" />} className="w-full !mt-5" multiline minRows={2} onChange={(e) => setDescriptionEn(e.target.value)} value={descriptionEn} />
            <Box className="mt-5">
                <MultipleSelectChip
                    data={majors}
                    title={intl.formatMessage({ id: 'linked_majors' })}
                    selected={majorIds}
                    onChange={(value) => setMajorIds(value)}
                />
            </Box>
            <Box className="relative w-full h-32 bg-gray-200 rounded-xl mt-5 flex flex-col items-center justify-center cursor-pointer overflow-hidden">
                {imagePreview ? (
                    <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                ) : (
                    <>
                        <img src={UploadImage} alt="" />
                        <Typography variant="body1" className="text-gray-700"><FormattedMessage id="add_image" /></Typography>
                    </>
                )}
                <input
                    type="file"
                    accept="image/*"
                    className="w-full h-full opacity-0 absolute cursor-pointer"
                    onChange={(e) => {
                        const file = e.target.files[0];
                        setImage(file);
                        if (file) setImagePreview(URL.createObjectURL(file));
                    }}
                />
            </Box>
            <Box className="flex justify-between mt-5 w-full max-sm:!flex-col max-sm:items-center" sx={{ flexDirection: language === 'en' && 'row-reverse' }}>
                <Button onClick={updateCategory} variant="contained" className="w-5/12 !text-white !font-bold hover:bg-blue-200 duration-300 max-sm:w-full">
                    {sendWait ? <CircularProgress size={20} color="inherit" /> : <><SaveOutlinedIcon /><FormattedMessage id="save" /></>}
                </Button>
                <Button onClick={onClickClose} variant="contained" className="w-5/12 !bg-gray-400 !text-gray-700 !font-bold hover:!bg-gray-200 duration-300 max-sm:w-full max-sm:!mt-2">
                    <FormattedMessage id="cancel" />
                </Button>
            </Box>
        </Box>
    );
}

export default UpdateCategory;
